import { takeLatest, call, put, takeEvery } from 'redux-saga/effects';
import _ from 'lodash';
import moment from 'moment';
import {
  FETCH_JOBS,
  fetchJobsSuccess,
  fetchJobsFailed,
  fetchingJobs,
  FETCH_JOB,
  fetchJobSuccess,
  fetchJobFailed,
  fetchingJob,
} from '../actions/jobs';

import { firestore, getTenantPath, getTenantId } from '../utils/firebase';
import { toArray, convertCollection, convertDocument, id } from '../utils/helper';
import { saveJobRoutine, saveJobResourcesRoutine, removeJobRoutine } from '../routines';
import { set, update, updateSaga } from './helper';
import { showError } from '../actions/dialogs';

function enrichJob(job, jobId) {
  const enrich = [];

  if (!job.id) {
    job.id = jobId;
  }

  job.start = job.start ? moment(job.start).toDate() : null;
  job.end = job.end ? moment(job.end).toDate() : null;

  if (job.customerId) {
    enrich.push(firestore.doc(getTenantPath(`customers/${job.customerId}`)).get()
      .then(convertDocument)
      .then((customer) => {
        if (customer) {
          job.customer = customer;
          job.primaryContact = job.customer.contacts ? job.customer.contacts[job.primaryContactId] : null;

          if (job.originId) {
            job.origin = job.customer.addresses[job.originId];
          }
          if (job.destinationId) {
            job.destination = job.customer.addresses[job.destinationId];
          }
        }
        return job;
      }));
  }

  if (job.jobContactId) {
    const ref = firestore.doc(getTenantPath(`employees/${job.jobContactId}`));
    enrich.push(ref.get()
      .then((snap) => {
        job.jobContact = snap.data();
        return job;
      }));
  }

  // if (job.leadId) {
  //   const ref = firebaseDb.ref(`employees/${job.leadId}`);
  //   // ref.keepSynced(true);
  //
  //   enrich.push(ref.once('value')
  //     .then((snap) => {
  //       job.lead = snap.data();
  //       return job;
  //     }));
  // }

  // TODO reinstate with resources
  // if (job.team) {
  //   enrich.push(Promise.all(_.keys(job.team).map((employeeId) => {
  //     return firebaseDb.ref(`employees/${employeeId}`).once('value')
  //       .then((employee) => {
  //         return Promise.all(job.team[employeeId].map((day) => {
  //           return firebaseDb.ref(`tenants/44317e68-4ec1-48fb-95d7-e8d0bc387f22/roles/${day.roleId}`) // TODO
  //             .once('value')
  //             .then((role) => {
  //               return {
  //                 start: day.start,
  //                 end: day.end,
  //                 role: role.data(),
  //               };
  //             });
  //         }))
  //           .then((days) => {
  //             return {
  //               employee: employee.data(),
  //               days,
  //             };
  //           });
  //       });
  //   }))
  //     .then((team) => {
  //       job.team = team;
  //     }));
  // }

  if (job.taskProfiles) {
    enrich.push(Promise.all(job.taskProfiles.map((profile) => {
      if (profile.items) {
        return Promise.all(_.keys(profile.items).map((itemTypeId) => {
          return firestore.doc(`itemtypes/${itemTypeId}`).get()
            .then((snap) => _.assign({ quantity: profile.items[itemTypeId] }, snap.data()));
        })).then((items) => {
          return _.assign(profile, { items, });
        });
      }
      return Promise.resolve(profile);
    })));
  }

  return Promise.all(enrich).then(() => {
    return job;
  });
}

function fetchJobs() {
  return firestore.collection('jobs')
    .where('tenantId', '==', getTenantId())
    .get()
    .then(convertCollection)
    .then((data) => {
      const jobs = toArray(data).filter((job) => !job.deleted);
      return Promise.all(jobs.map(enrichJob));
    })
    .catch((err) => {
      console.error(err);
    });
}

function* fetchJobsSaga() {
  try {
    yield put(fetchingJobs());
    const jobs = yield call(fetchJobs);
    yield put(fetchJobsSuccess(jobs));
  } catch (e) {
    yield put(fetchJobsFailed(e));
  }
}

function* watchFetchJobsSaga() {
  yield takeLatest(FETCH_JOBS, fetchJobsSaga);
}

function fetchJob(jobId) {
  return new Promise((resolve) => {
    firestore.doc(`jobs/${jobId}`).get().then((data) => {
      console.log('receiving job');
      return enrichJob(data.data(), jobId).then((job) => resolve(job));
    });
  });
}

function* fetchJobSaga(action) {
  try {
    yield put(fetchingJob());
    const job = yield call(fetchJob, action.jobId);
    yield put(fetchJobSuccess(job));
  } catch (e) {
    console.error('Failed to load job', e);
    yield put(fetchJobFailed(e));
  }
}

function* watchFetchJobSaga() {
  yield takeLatest(FETCH_JOB, fetchJobSaga);
}

function* watchSaveJobSaga() {
  // run fetchDataFromServer on every trigger action
  yield takeEvery(saveJobRoutine.TRIGGER, saveJobSaga);
}

function saveJob(job, tasks) {
  console.log('saveJob', job, tasks);
  if (!job.id) {
    job.id = id();
  }

  job.tenantId = job.tenantId || getTenantId();

  // job.customerId = job.customer.id; //_.pick(job.customer, ['id']);

  return set(`jobs/${job.id}`, _.omit(job, ['customer', 'primaryContact', 'tasks', 'resources']))
    .then(() => update(`jobs/${job.id}`, _.pick(job, ['resources'])))
    .then(() => job);
}

// const validate = (values) => {
//   console.log('validate', values);
//   const errors = {};
//   // if (!values.reference) {
//   //   errors.reference = 'Please enter a job reference';
//   // }
//   if (!values.customerId) {
//     errors.customerId = 'Please select a customer';
//   }
//   if (!values.profile) {
//     errors.profile = 'Please select a job profile';
//   }
//
//   console.log('validate-errors', errors);
//   return errors;
// };

function* saveJobSaga(action) {
  console.log('saveJobSaga', action);
  // const { job, tasks } = action.payload.toJS();
  const job = action.payload.values.toJS();
  console.log('job', job);

  // const errors = validate(job);
  // if (_.keys(errors).length !== 0) {
  //   yield put(saveJobRoutine.failure(new SubmissionError(errors)));
  //   return;
  // }

  try {
    // trigger request action
    yield put(saveJobRoutine.request(job));
    // perform request to '/some_url' to fetch some data
    const response = yield call(saveJob, job);
    const enrichedResponse = yield call(enrichJob, response);
    // if request successfully finished
    yield put(saveJobRoutine.success({ success: true, job: enrichedResponse }));
  } catch (err) {
    console.error(err);
    // if request failed
    yield put(showError('Unable to save job', err.message));
    yield put(saveJobRoutine.failure({ success: false }));
  } finally {
    // trigger fulfill action
    yield put(saveJobRoutine.fulfill(job));
  }
}

// TODO this is unlikely to work - IMPORTANT
function* watchSaveJobResourcesSaga() {
  // run fetchDataFromServer on every trigger action
  yield takeEvery(saveJobResourcesRoutine.TRIGGER,
    updateSaga(saveJobResourcesRoutine,
      (job) => `jobs/${job.id}`,
      (job) => ({ resources: job.resources })));
}

function* watchRemoveJobSaga() {
  // run fetchDataFromServer on every trigger action
  yield takeEvery(removeJobRoutine.TRIGGER, removeJobSaga);
}

function removeJob(job) {
  console.log('removeJob', job);
  return firestore.doc(`jobs/${job.id}`)
    .update({ deleted: { at: new Date() } })
    .then(() => job);
}

function* removeJobSaga(action) {
  const job = action.payload;
  console.log('removeJaobSaga', job);
  try {
    // trigger request action
    yield put(removeJobRoutine.request(job));
    // perform request to '/some_url' to fetch some data
    yield call(removeJob, job);
    // if request successfully finished
    yield put(removeJobRoutine.success({ success: true, job, }));
  } catch (err) {
    console.error(err);
    // if request failed
    yield put(removeJobRoutine.failure({ success: false }));
    yield put(showError('Unable to remove job', err.message));
  } finally {
    // trigger fulfill action
    yield put(removeJobRoutine.fulfill());
  }
}

export {
  fetchJobsSaga,
  watchFetchJobsSaga,
  fetchJobSaga,
  watchFetchJobSaga,
  watchSaveJobSaga,
  watchSaveJobResourcesSaga,
  watchRemoveJobSaga,
};



// WEBPACK FOOTER //
// ./src/sagas/jobs.js